import { AlternateEmail } from "@mui/icons-material"
import { Box, Button, Dialog, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip } from "@mui/material"
import { useTranslation } from "react-i18next"
import { CloseIconButton } from "../Utilities"
import { useKeycloak } from "../Keycloak"
import { useMemo, useState } from "react"
import { useSendSupportEmailMutation } from "../../api/graphqlApi"
import { LoadingButton } from "@mui/lab"
import CameraSelection from "../CameraSelection"
import FormField from "../FormField"

export default function Support({ invertColors, isOpen, handleOpen, handleClose }) {

    const { t } = useTranslation()
    const { isLoggedIn, isUsersPortal, getEmail } = useKeycloak()
    const { CAMERAS, DEBUG } = window.conf

    const isAuthorized = isLoggedIn() && isUsersPortal()
    const moreThanOneCam = CAMERAS.length > 1

    const defaultStates = useMemo(() => ({
        cameraIds: CAMERAS?.length ? [CAMERAS[0].id] : [],
        subject: 'technical',
        issue: 'function',
        notes: '',
        email: getEmail() || ''
    }), [CAMERAS, getEmail])

    const [cameraIds, setCameraIds] = useState(defaultStates.cameraIds)
    const [subject, setSubject] = useState(defaultStates.subject)
    const [issue, setIssue] = useState(defaultStates.issue)
    const [notes, setNotes] = useState(defaultStates.notes)
    const [email, setEmail] = useState(defaultStates.email)
    const [emailError, setEmailError] = useState(true)
    const [sendSupportEmail, { isLoading } ] = useSendSupportEmailMutation()

    const disableForm = isLoading

    const onChangeSubject = (event) => {
        const newSubject = event.target.value
        setSubject(newSubject)
        setIssue(newSubject === 'technical' ? "function" : "extend")
    }

    const onChangeIssue = (event) => setIssue(event.target.value)

    const onChangeNotes = (event) => setNotes(event.target.value)

    const onChangeEmail = (event) => setEmail(event.target.value)

    const availableIssues = subject === 'technical'
        ? ["function", "focus", "zoom", "mask", "other"]
        : ["extend", "deregister", "offer", "other"]

    const resetForm = () => {
        const { cameraIds, subject, issue, notes, email } = defaultStates
        setCameraIds(cameraIds)
        setSubject(subject)
        setIssue(issue)
        setNotes(notes)
        setEmail(email)
        setEmailError(true)
    }

    const onOpenInternally = () => {
        resetForm()
        handleOpen()
    }

    const onSubmit = async() => {
        try {
            const message = {
                email,
                subject: t(`support.${subject}`, { lng: "de" }),
                issue: t(`support.${issue}`, { lng: "de" }),
                notes: notes && notes.length ? notes : "-",
                cameras: moreThanOneCam && cameraIds.length ? cameraIds.map(id => CAMERAS[id]?.featureDetailId) : [CAMERAS[0].featureDetailId]
            }
            
            DEBUG && console.log("[Support] Send email request:", message);

            const result = await sendSupportEmail(message).unwrap();
        
            DEBUG && console.log("[Support] Send email result:", result);

            handleClose();
        } catch (error) {
            console.error("[Support] Failed to send mail:", error);
        }
    }

    return isAuthorized && (
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Tooltip title={t("support.support")} arrow>
                <IconButton
                    color="inherit"
                    sx={{ 
                        p: 0,
                        ml: "16px",
                        mb: "1.5px",
                        color: invertColors ? 'white' : 'primary.main'
                    }}
                    onClick={onOpenInternally}
                >
                    <AlternateEmail sx={{ fontSize: '1.2rem' }} />
                </IconButton>
            </Tooltip>

            <Dialog
                onClose={handleClose}
                open={isOpen}
                fullWidth
                maxWidth="xs"
            >

                <CloseIconButton onClick={handleClose} />

                <Grid container sx={{ p: 2 }}>

                    <Grid item xs={12} sx={{ pb: 1 }} />

                    <Grid item xs={12}>
                        <FormField
                            id="email"
                            label={t("support.mail")}
                            required
                            value={email}
                            onChange={onChangeEmail}
                            validate="email"
                            onValidationChange={setEmailError}
                            autoFocus={!Boolean(email)}
                            type="email"
                            disabled={disableForm}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth sx={{ pb: 2 }}>
                            <InputLabel id="select-subject-label">{t("support.subject")}</InputLabel>
                            <Select
                                labelId="select-subject-label"
                                id="select-subject"
                                value={subject}
                                label={t("support.subject")}
                                onChange={onChangeSubject}
                                disabled={disableForm}
                            >
                                <MenuItem value="technical">{t("support.technical")}</MenuItem>
                                <MenuItem value="contractual">{t("support.contractual")}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth sx={{ pb: 2 }}>
                            <InputLabel id="select-issue-label">{t("support.issue")}</InputLabel>
                            <Select
                                labelId="select-issue-label"
                                id="select-issue"
                                value={issue}
                                label={t("support.issue")}
                                onChange={onChangeIssue}
                                disabled={disableForm}
                            >
                                {availableIssues.map(key => (
                                    <MenuItem key={key} value={key}>
                                        {t(`support.${key}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {moreThanOneCam && (
                        <Grid item xs={12}>
                            <CameraSelection
                                value={cameraIds}
                                onChange={setCameraIds}
                                multiple
                                disabled={disableForm}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FormField
                            id="notes"
                            label={t("support.notes")}
                            multiline
                            rows={4}
                            value={notes}
                            onChange={onChangeNotes}
                            autoFocus={Boolean(email)}
                            disabled={disableForm}
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: 'flex', 
                            flexDirection: 'row', 
                            justifyContent: 'space-between'
                        }}
                    >

                        <Button
                            variant="outlined"
                            sx={{
                                width: 100, 
                                mr: 1, 
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                            onClick={handleClose}
                            disabled={disableForm}
                        >
                            {t("actions.cancel")}
                        </Button>

                        <LoadingButton
                            variant="outlined"
                            sx={{ 
                                width: 100, 
                                mr: 0, 
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                            onClick={onSubmit}
                            loading={isLoading}
                            disabled={!email || emailError}
                        >
                            {t("actions.send")}
                        </LoadingButton>

                    </Grid>

                </Grid>

            </Dialog>
        </Box>
    )
}